import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import { Badge } from "@/app/admin/_common/ui/badge";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import CuoConfirmDialog from "@/app/admin/_common/CuoConfirmDialog";
import ShowThumbnail from "@/app/admin/_common/ShowThumbnail";
import { cn } from "@/app/_utils/cn";
import { dayjsTz } from "@/app/_utils/dayjs";
import type { Location } from "@/app/_services/LocationService";
import { MoreHorizontal, Pencil, Trash2, MapPinOff, Building2, Eye } from "lucide-react";

const currencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export default function LocationList({
  locations,
  selectedLocation,
  onSelectLocation,
  onEdit,
  onDelete,
}: {
  locations: Location[];
  selectedLocation: Location | null;
  onSelectLocation: (location: Location) => void;
  onEdit: (location: Location) => void;
  onDelete: (location: Location) => void;
}) {
  const [pendingDelete, setPendingDelete] = useState<Location | null>(null);
  const navigate = useNavigate();

  return (
    <div className="flex h-full flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border">
        <Table
          containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
          className={cn("min-w-[760px] table-fixed", locations.length === 0 && "h-full")}
        >
          <TableHeader>
            <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
              <TableHead className="h-8 w-[280px] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Name
              </TableHead>
              <TableHead className="h-8 w-20 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Shows
              </TableHead>
              <TableHead className="h-8 w-24 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Revenue
              </TableHead>
              <TableHead className="h-8 w-1/3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Last show
              </TableHead>
              <TableHead className="h-8 w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {locations.length === 0 ? (
              <TableRow className="h-full hover:bg-transparent">
                <TableCell colSpan={5} className="h-full p-0">
                  <CuoEmpty icon={Building2} text="No locations" />
                </TableCell>
              </TableRow>
            ) : (
              locations.map((location) => (
              <TableRow
                key={location.id}
                className={cn(
                  // Overrides TableCell's default p-4 once, rather than on all five cells.
                  "cursor-pointer [&>td]:py-1.5",
                  selectedLocation?.id === location.id && "bg-muted"
                )}
                onClick={() => onSelectLocation(location)}
              >
                <TableCell>
                  <div className="min-w-0 leading-tight">
                    <div className="flex items-center gap-1.5">
                      <span
                        className="cursor-pointer truncate text-[13px] text-foreground hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/locations/${location.id}`);
                        }}
                      >
                        {location.name}
                      </span>
                      {location.latitude == null && (
                        <Badge
                          variant="outline"
                          title="No coordinates"
                          className="flex-none border-border p-1 text-muted-foreground"
                        >
                          <MapPinOff className="size-3" />
                        </Badge>
                      )}
                    </div>
                    <div className="truncate text-[12px] text-muted-foreground">
                      {[
                        location.address_line_1,
                        location.address_line_2,
                        location.city,
                        location.state,
                        location.zip,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap text-center text-[12.5px] text-muted-foreground">
                  {location.show_count ?? 0}
                </TableCell>
                <TableCell className="whitespace-nowrap text-right text-[12.5px] text-muted-foreground">
                  {currencyFormat.format(location.total_revenue ?? 0)}
                </TableCell>
                <TableCell>
                  {location.last_show_date ? (
                    <div className="flex items-center gap-2">
                      <ShowThumbnail
                        src={location.last_show_img_url}
                        alt={location.last_show_name ?? location.name}
                        // Sets the row height floor now that padding is py-1.5.
                        style={{
                          height: 28,
                          width: 28,
                          borderRadius: 6,
                          flexShrink: 0,
                        }}
                      />
                      <div className="min-w-0">
                        <div className="truncate text-[12.5px] text-foreground">
                          {location.last_show_name ?? "—"}
                        </div>
                        <div className="truncate text-[11.5px] text-muted-foreground">
                          {dayjsTz(location.last_show_date).format("MMM D, YYYY")}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="text-[12.5px] text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="ml-auto flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                      >
                        <MoreHorizontal className="size-4 shrink-0" size={16} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/admin/locations/${location.id}`)}>
                        <Eye className="mr-2 size-4" />
                        View details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onEdit(location)}>
                        <Pencil className="mr-2 size-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600 focus:bg-red-50 focus:text-red-700"
                        onClick={() => setPendingDelete(location)}
                      >
                        <Trash2 className="mr-2 size-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
              ))
            )}
          </TableBody>
        </Table>

      <CuoConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Delete location"
        message="This action cannot be undone. Are you sure you want to delete this location?"
        onConfirm={async () => {
          if (pendingDelete) await onDelete(pendingDelete);
        }}
        icon={<Trash2 />}
      />
    </div>
  );
}
